import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
// Header is provided by AuthenticatedRoute wrapper

interface VerificationData {
  verification_code: string;
  requesting_full_name: string;
  requesting_email: string;
  company_name: string;
  expires_at: string;
}

const VerifyCompanyJoin: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [verification, setVerification] = useState<VerificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  useEffect(() => {
    if (code) {
      fetchVerificationDetails();
    }
  }, [code, fetchVerificationDetails]);

  const fetchVerificationDetails = useCallback(async () => {
    try {
      // This would typically come from a URL with verification details
      // For now, we'll just validate the code exists
      if (!code) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      // In a real implementation, you'd fetch verification details from the backend
      // For demo purposes, we'll set mock data
      setVerification({
        verification_code: code,
        requesting_full_name: 'John Doe',
        requesting_email: 'john@example.com',
        company_name: 'Example Company',
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      });
      setLoading(false);
    } catch {
      setError('Failed to load verification details');
      setLoading(false);
    }
  }, [code]);

  const handleApprove = async () => {
    if (!verification) return;

    setProcessing(true);
    try {
      const response = await supabase.functions.invoke('handleCompanyVerification', {
        body: {
          verification_code: verification.verification_code,
          action: 'approve'
        }
      });

      if (response.error) throw response.error;

      if (response.data.success) {
        // Show success message and redirect
        alert(`Successfully approved ${verification.requesting_full_name} to join your company!`);
        navigate('/dashboard');
      } else {
        throw new Error(response.data.error || 'Approval failed');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!verification) return;

    setProcessing(true);
    try {
      const response = await supabase.functions.invoke('handleCompanyVerification', {
        body: {
          verification_code: verification.verification_code,
          action: 'reject',
          rejection_reason: rejectionReason
        }
      });

      if (response.error) throw response.error;

      if (response.data.success) {
        // Show success message and redirect
        alert(`Request from ${verification.requesting_full_name} has been rejected.`);
        navigate('/dashboard');
      } else {
        throw new Error(response.data.error || 'Rejection failed');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading verification details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !verification) {
    return (
      <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-6">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-medium text-red-800">
                Verification Error
              </h3>
            </div>
            <p className="text-red-700 mt-2">{error || 'Invalid verification link'}</p>
          </div>
        </div>
      </div>
    );
  }

  const expiresAt = new Date(verification.expires_at);
  const isExpired = expiresAt < new Date();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Company Join Request
            </h3>

            {isExpired ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Verification Expired
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This verification request expired on {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString()}.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Join Request Details
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p><strong>{verification.requesting_full_name}</strong> ({verification.requesting_email}) wants to join <strong>{verification.company_name}</strong>.</p>
                          <p className="mt-1">Request expires: {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    <p>As a member of {verification.company_name}, you can approve or reject this request.</p>
                    <p className="mt-2">
                      <strong>Note:</strong> Once approved, {verification.requesting_full_name} will have access to your company's campaigns and analytics.
                    </p>
                  </div>

                  {!showRejectionForm ? (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleApprove}
                        disabled={processing}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processing ? 'Processing...' : 'Approve Request'}
                      </button>
                      <button
                        onClick={() => setShowRejectionForm(true)}
                        disabled={processing}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reject Request
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="rejection_reason" className="block text-sm font-medium text-gray-700">
                          Rejection Reason (Optional)
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="rejection_reason"
                            name="rejection_reason"
                            rows={3}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Optional: Provide a reason for rejection..."
                          />
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleReject}
                          disabled={processing}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {processing ? 'Processing...' : 'Confirm Rejection'}
                        </button>
                        <button
                          onClick={() => {
                            setShowRejectionForm(false);
                            setRejectionReason('');
                          }}
                          disabled={processing}
                          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCompanyJoin;